/** Local city landing pages — patio cover SEO. */
import { PRICING_COPY } from './pricingCopy.js';

export const CITY_PAGE_ORDER = [
  'vancouver',
  'burnaby',
  'richmond',
  'surrey',
  'delta',
  'coquitlam',
  'langley',
  'north-vancouver',
  'west-vancouver',
  'new-westminster',
  'maple-ridge',
  'pitt-meadows',
  'port-coquitlam',
  'port-moody',
  'abbotsford',
  'white-rock',
];

export const CITY_PAGES = {
  vancouver: {
    id: 'vancouver',
    path: '/patio-covers-vancouver',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Vancouver | Fast Estimate | LoomiHome Patios',
    metaDescription:
      'Patio covers in Vancouver — compare aluminum, glass, and combo patio covers online. Sunroom pricing is confirmed after a free Lower Mainland on-site measurement.',
    h1: 'Patio Covers in Vancouver',
    intro:
      'If you are searching for patio covers in Vancouver, you probably want two things: protection from the rain and a backyard that still feels bright and usable. We help you skip the slow back-and-forth with traditional quotes — get an online planning range from our Design & Price tool in about a minute, compare patio cover types, then book a free on-site measurement to confirm details.',
    highlights: [
      'Rough ballpark pricing before anyone steps on your property',
      'Compare aluminum, glass, skyline combo, and sunroom directions in one flow',
      'Built for Lower Mainland weather — shade, rain, and longevity',
      'Free on-site visit after you have a range that makes sense',
    ],
    localAngle:
      'From Kits to East Van to the slopes of North Van, Vancouver lots and bylaws vary. Use the online Design & Price tool to compare options for your size; we confirm site details during a free measurement.',
    pricingNote:
      'Every project is different, but most homeowners want a starting range first. We use your approximate size and city to give a sensible ballpark; final pricing always follows an accurate measure.',
    faqs: [
      {
        q: 'How fast can I get a patio cover estimate in Vancouver?',
        a: 'The online Design & Price tool gives you a planning range in about a minute after you enter your city, size, and cover type. We can then book a free on-site measurement.',
      },
      {
        q: 'Can I get pricing before booking a site visit?',
        a: 'Yes. That is how we set things up. Rough quote first, free on-site measurement second when you want to move forward.',
      },
      {
        q: 'Do patio covers help with Vancouver rain?',
        a: 'A well-designed cover keeps your patio usable through wet months. Aluminum and combo systems are especially popular when rain protection is the main goal; glass options add light while still shedding water.',
      },
      {
        q: 'What cities do you serve besides Vancouver?',
        a: 'We work across the Lower Mainland — Burnaby, Richmond, Surrey, Delta, Coquitlam, and nearby. Same fast estimate process everywhere we go.',
      },
      {
        q: 'How much does a patio cover cost in Vancouver?',
        a: 'It depends on size, product (aluminum, glass, or skyline combo), attachment, and site access. Our Design & Price tool gives a planning total from rough dimensions; the Vancouver patio cover cost guide explains what moves the price, and free measurement confirms the formal quote.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-vancouver', label: 'Patio cover contractor in Vancouver' },
      { path: '/glass-patio-covers-vancouver', label: 'Glass patio covers in Vancouver' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
      { path: '/projects/vancouver-aluminum-patio-cover', label: 'Vancouver aluminum patio cover project' },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Glass patio cover on a Vancouver home with natural light under tempered glass roof',
      caption:
        'Vancouver glass patio cover — bright rain protection for walkout decks and rear living areas. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/vancouver-glass-patio-cover',
    },
  },
  burnaby: {
    id: 'burnaby',
    path: '/patio-covers-burnaby',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Burnaby | LoomiHome Patios',
    metaDescription:
      'Patio covers in Burnaby — fast online estimate, compare cover types, free measurement. Rain-ready shade for Metrotown and nearby neighbourhoods.',
    h1: 'Patio Covers in Burnaby',
    intro:
      'Burnaby homes range from tight lots near Metrotown to roomier pockets near Deer Lake. Wherever you are, use our Design & Price tool to compare aluminum, glass, or combo patio cover planning ranges, then schedule a free on-site measurement if the numbers feel right.',
    highlights: [
      'Fast ballpark — no waiting days for a first reply',
      'Options for smaller city lots and larger suburban yards',
      'Rain and UV protection that fits Pacific Northwest weather',
      'Straight path from rough quote to measured final price',
    ],
    localAngle:
      'We are on the road across Burnaby regularly. Whether you want a simple aluminum cover or something brighter with glass, you can compare directions online before committing to a visit.',
    pricingNote:
      'Share approximate dimensions and your part of Burnaby — we will give a realistic starting range. Final numbers always follow an on-site measure.',
    faqs: [
      {
        q: 'Do you install patio covers across all of Burnaby?',
        a: 'Yes — north and south Burnaby, Brentwood, Edmonds, Deer Lake area, and surrounding. Same estimate flow for each.',
      },
      {
        q: 'How much does a patio cover cost in Burnaby?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Burnaby cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'How does your estimate compare to calling contractors one by one?',
        a: 'You get a rough range almost immediately instead of playing phone tag. If the range works, we come out to measure for free and tighten the quote.',
      },
      {
        q: 'What cover type works best for rainy Burnaby weather?',
        a: 'Many Burnaby homeowners lean aluminum or combo covers for solid rain protection; glass is great when you want more natural light. We walk you through trade-offs in the online Design & Price tool.',
      },
      {
        q: 'Is the on-site measurement free?',
        a: 'Yes — after your ballpark estimate, we can book a free visit to measure and confirm details. No obligation.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-burnaby', label: 'Patio cover contractor in Burnaby' },
      { path: '/aluminum-patio-covers-burnaby', label: 'Aluminum patio covers in Burnaby' },
      { path: '/glass-patio-covers-burnaby', label: 'Glass patio covers in Burnaby' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
      { path: '/patio-cover-showroom-burnaby', label: 'Our Burnaby showroom' },
      { path: '/projects/burnaby-aluminum-patio-cover', label: 'Burnaby aluminum project' },
      { path: '/projects/burnaby-glass-patio-cover', label: 'Burnaby glass project' },
      { path: '/sunrooms-burnaby', label: 'Sunrooms in Burnaby' },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Aluminum patio cover on a Burnaby backyard patio for rain protection',
      caption:
        'Burnaby aluminum patio cover — practical rain protection for Metrotown and Deer Lake backyards. Compare glass in the online Design & Price tool using the same size.',
      projectPath: '/projects/burnaby-aluminum-patio-cover',
    },
  },
  richmond: {
    id: 'richmond',
    path: '/patio-covers-richmond',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Richmond | Fast Estimate | LoomiHome Patios',
    metaDescription:
      'Patio covers in Richmond — compare aluminum, glass, and combo options. Fast ballpark quote online, then free on-site measurement.',
    h1: 'Patio Covers in Richmond',
    intro:
      'Richmond’s flat terrain and coastal weather make covered patios especially useful — you get shade in summer and a drier outdoor space the rest of the year. We focus on speed: a rough patio cover quote in about a minute, clear comparison between product types, and a free measurement visit when you want exact pricing.',
    highlights: [
      'Designed around wet, windy days and sunny afternoons',
      'Modern looks that suit newer Richmond builds',
      'No pressure — estimate first, visit second',
      'Same team serving Richmond and nearby cities',
    ],
    localAngle:
      'From Steveston to City Centre, lot sizes and styles differ. The online Design & Price tool helps you compare options — entertainment space, kid-friendly dry zone, or a cleaner modern backyard — before we meet in person.',
    pricingNote:
      'Ballpark pricing reflects approximate size and product family. We keep language plain so you know what to expect before we measure.',
    faqs: [
      {
        q: 'Can I get a patio cover quote for Richmond without a site visit first?',
        a: 'You can get a planning range from the online Design & Price tool first. A site visit is for precise measurements and final numbers — and that visit is free when you are ready.',
      },
      {
        q: 'How much does a patio cover cost in Richmond?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Richmond cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'Glass or aluminum for a Richmond home near the water?',
        a: 'Both can work. Aluminum is often the value-first, high-rain choice; glass adds light and a premium look. We help you compare for your specific patio.',
      },
      {
        q: 'Do you serve all Richmond neighbourhoods?',
        a: 'Yes — including Steveston, Broadmoor, Burkeville, East Cambie, and surrounding areas.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-richmond', label: 'Patio cover contractor in Richmond' },
      { path: '/aluminum-patio-covers-richmond', label: 'Aluminum patio covers in Richmond' },
      { path: '/glass-patio-covers-richmond', label: 'Glass patio covers in Richmond' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
      { path: '/projects/richmond-aluminum-patio-cover', label: 'Richmond aluminum patio project' },
      { path: '/projects/richmond-glass-patio-cover', label: 'Richmond glass project' },
    ],
    caseStudy: {
      image: '/house/before-after/richmond-carport-after.png',
      alt: 'Aluminum carport-style patio cover on a Richmond home near Steveston or Broadmoor',
      caption:
        'Richmond aluminum cover — rain-ready protection for side yards and back patios. Compare glass in the online Design & Price tool using the same footprint.',
      projectPath: '/projects/richmond-aluminum-carport-cover',
    },
  },
  surrey: {
    id: 'surrey',
    path: '/patio-covers-surrey',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Surrey | Fast Estimate | LoomiHome Patios',
    metaDescription:
      'Patio covers in Surrey — get a planning range online for aluminum, glass, and combo covers. Sunroom pricing is confirmed after a free on-site measurement.',
    h1: 'Patio Covers in Surrey',
    intro:
      'Surrey lots often have room to spread out — and homeowners here still want the same thing: honest pricing without the runaround. Use our online Design & Price tool for a patio cover planning range, compare styles side by side, then invite us for a free on-site measurement when you are serious about moving forward.',
    highlights: [
      'Great for larger patios and growing families',
      'Rough quote in minutes, not days',
      'Sunrooms and covers in one conversation if you are undecided',
      'Local Lower Mainland crew — we know the weather',
    ],
    localAngle:
      'From Fleetwood to South Surrey, we see everything from compact townhome patios to wide back decks. Enter your city, approximate size, and cover type in the online Design & Price tool to get a planning range.',
    pricingNote:
      'Surrey projects vary by size and finish level. We start with a range you can react to; detailed pricing follows measurement.',
    faqs: [
      {
        q: 'How much does a patio cover cost in Surrey?',
        a: 'It depends on size, product type, and site conditions. Our Design & Price tool gives you a sensible ballpark for your situation; our Surrey cost guide also explains local cost factors.',
      },
      {
        q: 'Do you cover North Surrey and Cloverdale too?',
        a: 'Yes — we serve Surrey broadly, including Guildford, Newton, Cloverdale, Fleetwood, and South Surrey.',
      },
      {
        q: 'Should I compare glass, aluminum, or a skyline combo in Surrey?',
        a: 'Aluminum is usually the rain-first budget option; glass keeps more light; skyline combo mixes both. We can compare all three in the Design & Price tool using the same approximate size.',
      },
      {
        q: 'Sunroom or patio cover — how do I compare them?',
        a: 'Use the Design & Price tool to compare patio cover options. We review sunroom buildable area and confirm pricing during a free on-site measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/skyline-combo-patio-covers-surrey', label: 'Skyline combo covers in Surrey' },
      { path: '/patio-cover-contractor-surrey', label: 'Patio cover contractor in Surrey' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
      { path: '/aluminum-patio-covers-surrey', label: 'Aluminum patio covers in Surrey' },
      { path: '/glass-patio-covers-surrey', label: 'Glass patio covers in Surrey' },
      { path: '/projects/surrey-glass-patio-cover', label: 'Surrey glass project' },
      { path: '/projects/surrey-aluminum-patio-cover', label: 'Surrey aluminum project' },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Skyline combo patio cover on a large Surrey backyard with glass and V-panel sections',
      caption:
        'Surrey skyline combo cover — balanced light and shade for larger backyards. Compare aluminum and full glass in the online Design & Price tool.',
      projectPath: '/projects/surrey-skyline-combo-patio-cover',
    },
  },
  delta: {
    id: 'delta',
    path: '/patio-covers-delta',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Delta | Fast Estimate | LoomiHome Patios',
    metaDescription:
      'Patio covers in Delta — Ladner, Tsawwassen, North Delta. Fast rough quote online, compare cover types, free measurement for local weather.',
    h1: 'Patio Covers in Delta',
    intro:
      'Delta gets its share of wind off the water and wet shoulder seasons — a solid patio cover keeps your deck usable year-round. We make the first step easy: a fast online ballpark for patio cover pricing, a clear comparison of aluminum, glass, and combo systems, and a no-charge on-site measurement when you want firm numbers.',
    highlights: [
      'Weather-aware recommendations for coastal-adjacent homes',
      'Rough pricing before we drive out',
      'Straight answers — no endless email chains',
      'Free measurement when you are ready to proceed',
    ],
    localAngle:
      'Ladner, Tsawwassen, and North Delta each have different lot styles. Tell us your area and how you use the patio — we will tailor the ballpark conversation.',
    pricingNote:
      'Exposure and attachment details affect final cost. The online estimate gets you in the right band; we refine on site.',
    faqs: [
      {
        q: 'Do you install patio covers in Tsawwassen and Ladner?',
        a: 'Yes — all of Delta, including Ladner, Tsawwassen, and North Delta.',
      },
      {
        q: 'How much does a patio cover cost in Delta?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Delta cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'Are patio covers worth it in windy areas?',
        a: 'Engineering and attachment matter. We discuss your situation in chat and confirm structure and hardware on the measurement visit.',
      },
      {
        q: 'Can I compare cover types before choosing?',
        a: 'Absolutely — that is the point of our process. Ballpark each direction, then decide what to measure.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-delta', label: 'Patio cover contractor in Delta' },
      { path: '/aluminum-patio-covers-delta', label: 'Aluminum patio covers in Delta' },
      { path: '/glass-patio-covers-delta', label: 'Glass patio covers in Delta' },
      { path: '/patio-cover-cost-delta', label: 'Patio cover cost in Delta' },
      { path: '/projects/delta-aluminum-patio-cover', label: 'Delta aluminum project' },
      { path: '/projects/delta-glass-patio-cover', label: 'Delta glass project' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
    caseStudy: {
      image: '/house/before-after/delta-aluminum-after.png',
      alt: 'Aluminum patio cover on a Delta backyard patio in Ladner or North Delta',
      caption:
        'Delta aluminum patio cover — dependable rain protection for coastal-adjacent family patios. Compare glass in the online Design & Price tool using the same size.',
      projectPath: '/projects/delta-aluminum-patio-cover',
    },
  },
  coquitlam: {
    id: 'coquitlam',
    path: '/patio-covers-coquitlam',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Coquitlam | LoomiHome Patios',
    metaDescription:
      'Patio covers in Coquitlam — Burke Mountain, Austin Heights, and nearby. Quick online estimate, compare aluminum, glass, and combo covers.',
    h1: 'Patio Covers in Coquitlam',
    intro:
      'Coquitlam’s mix of hillside homes and established neighbourhoods means every patio is a little different. Use the online Design & Price tool to get a patio cover planning range and compare aluminum, glass, and skyline combo options. We review sunroom buildable area during a free on-site measurement.',
    highlights: [
      'Helpful on sloped lots and standard yards alike',
      'Fast first number — then free measurement',
      'Compare products without sales pressure',
      'Serving Coquitlam plus nearby Tri-Cities demand',
    ],
    localAngle:
      'From Burke Mountain to Maillardville, we have measured a lot of Coquitlam patios. A few photos help us review the site before we visit; use the online Design & Price tool for a planning range based on your dimensions.',
    pricingNote:
      'Slope, height, and attachment can change cost. We explain that plainly after the initial range.',
    faqs: [
      {
        q: 'Do you work in Port Moody and Port Coquitlam too?',
        a: 'Yes — we serve the Tri-Cities including Port Coquitlam and Port Moody. See our dedicated city pages or ask in chat if you are on the border.',
      },
      {
        q: 'How fast is the first estimate?',
        a: 'Use the Design & Price tool for a patio cover planning range; formal pricing follows a free on-site measurement.',
      },
      {
        q: 'What if I want a sunroom instead of a cover?',
        a: 'We can discuss both. Many Coquitlam homeowners compare enclosed sunroom vs open cover for budget and use-case.',
      },
      {
        q: 'How much does a patio cover cost in Coquitlam?',
        a: 'It depends on size, product, slope or deck height, and attachment. Our Design & Price tool gives a planning total from rough dimensions; the Coquitlam patio cover cost guide explains what moves the price, and free measurement confirms the formal quote.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-coquitlam', label: 'Patio cover contractor in Coquitlam' },
      { path: '/aluminum-patio-covers-coquitlam', label: 'Aluminum patio covers in Coquitlam' },
      { path: '/glass-patio-covers-coquitlam', label: 'Glass patio covers in Coquitlam' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
      { path: '/projects/coquitlam-aluminum-patio-cover', label: 'Coquitlam aluminum project' },
      { path: '/projects/coquitlam-glass-patio-cover', label: 'Coquitlam glass project' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Glass patio cover on a Coquitlam backyard patio with natural light under tempered glass',
      caption:
        'Coquitlam glass patio cover — bright rain protection for Burke Mountain and Maillardville decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/coquitlam-glass-patio-cover',
    },
  },
  langley: {
    id: 'langley',
    path: '/patio-covers-langley',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Langley | Fast Estimate | LoomiHome Patios',
    metaDescription:
      'Patio covers in Langley — Willoughby, Walnut Grove, Brookswood, and nearby. Compare aluminum, glass, and combo covers online; sunroom pricing follows a free on-site measurement.',
    h1: 'Patio Covers in Langley',
    intro:
      'Langley homeowners often want a patio that works through wet springs and sunny summers without months of quote chasing. Enter your city, approximate size, and cover type in the online Design & Price tool to see a patio cover planning range. Sunroom pricing is confirmed after a free on-site measurement of the buildable area and site conditions.',
    highlights: [
      'Helpful for larger Langley lots and townhome patios',
      'Ballpark pricing before anyone visits your home',
      'Compare cover types in one conversation',
      'Lower Mainland crew familiar with Fraser Valley weather',
    ],
    localAngle:
      'From Willoughby to Fort Langley, lot sizes and bylaws vary. Use the online Design & Price tool for a patio cover planning range. We discuss sunroom layouts separately and confirm pricing after a free on-site measurement.',
    pricingNote:
      'Attachment, height, and finish level change the final number. We explain that clearly after your initial ballpark.',
    faqs: [
      {
        q: 'Do you serve Walnut Grove and Willoughby?',
        a: 'Yes — we regularly work across Langley, including Walnut Grove, Willoughby, Brookswood, and nearby areas.',
      },
      {
        q: 'How much does a patio cover cost in Langley?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Langley cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'How fast is the first patio cover estimate?',
        a: 'The online Design & Price tool gives most homeowners a planning range in about a minute after they enter their city and approximate size.',
      },
      {
        q: 'Can I compare a sunroom and a patio cover?',
        a: 'Absolutely. Many Langley homeowners compare both before booking a free measurement visit.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-langley', label: 'Patio cover contractor in Langley' },
      { path: '/aluminum-patio-covers-langley', label: 'Aluminum patio covers in Langley' },
      { path: '/glass-patio-covers-langley', label: 'Glass patio covers in Langley' },
      { path: '/patio-cover-cost-langley', label: 'Patio cover cost in Langley' },
      { path: '/projects/langley-aluminum-patio-cover', label: 'Langley aluminum project' },
      { path: '/projects/langley-glass-patio-cover', label: 'Langley glass project' },
    ],
    caseStudy: {
      image: '/house/Aluminum/p27.jpg',
      alt: 'Fraser Valley aluminum patio cover — planning reference for Langley Willoughby and Walnut Grove lots',
      caption:
        'Aluminum patio cover example used for Langley planning — practical rain protection for Willoughby and Walnut Grove backyards. Compare glass in the online Design & Price tool using the same size.',
      projectPath: '/projects/langley-aluminum-patio-cover',
    },
  },
  'north-vancouver': {
    id: 'north-vancouver',
    path: '/patio-covers-north-vancouver',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'North Vancouver Patio Covers | LoomiHome Patios',
    metaDescription:
      'Patio covers in North Vancouver — Lynn Valley, Lower Lonsdale, Edgemont, and nearby. Fast rough quote, compare aluminum, glass, and combo.',
    h1: 'Patio Covers in North Vancouver',
    intro:
      'North Vancouver homes often need covers that handle rain, tree debris, and sloped lots. Get an online planning range from our Design & Price tool, compare aluminum, glass, or combo options, then book a free on-site measurement when you want exact pricing.',
    highlights: [
      'Rain-ready covers for North Shore weather',
      'Helpful on sloped yards and raised decks',
      'Ballpark pricing before a site visit',
      'Serving North Van and the wider Lower Mainland',
    ],
    localAngle:
      'From Lower Lonsdale to Lynn Valley and the mountainside, attachment and height vary. Chat narrows product type and budget before we measure.',
    pricingNote:
      'Slope, span, and finish level change the final number. We explain that after your initial ballpark.',
    faqs: [
      {
        q: 'Do you install patio covers across North Vancouver?',
        a: 'Yes — we serve North Vancouver regularly along with Vancouver, Burnaby, and nearby cities.',
      },
      {
        q: 'How much does a patio cover cost in North Vancouver?',
        a: 'Our Design & Price tool gives a planning total for your size. See the North Vancouver cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'How fast is the first estimate?',
        a: 'Use the Design & Price tool for a patio cover planning range; formal pricing follows a free on-site measurement.',
      },
      {
        q: 'Is glass worth comparing on the North Shore?',
        a: 'Yes when you want light on sloped decks — aluminum is still the practical rain-first starting point. Compare their features in chat; use the Design & Price tool for patio cover planning ranges.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-north-vancouver', label: 'Patio cover contractor in North Vancouver' },
      { path: '/aluminum-patio-covers-north-vancouver', label: 'Aluminum patio covers in North Vancouver' },
      { path: '/glass-patio-covers-north-vancouver', label: 'Glass patio covers in North Vancouver' },
      { path: '/patio-cover-cost-north-vancouver', label: 'Patio cover cost in North Vancouver' },
      { path: '/projects/north-vancouver-aluminum-patio-cover', label: 'North Vancouver aluminum project' },
      { path: '/projects/north-vancouver-glass-patio-cover', label: 'North Vancouver glass project' },
    ],
    caseStudy: {
      image: '/house/glass/glass17.jpg',
      alt: 'North Shore glass patio cover — planning reference for North Vancouver walkout decks',
      caption:
        'Glass patio cover example used for North Vancouver planning — bright rain protection for North Shore sloped lots. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/north-vancouver-glass-patio-cover',
    },
  },
  'west-vancouver': {
    id: 'west-vancouver',
    path: '/patio-covers-west-vancouver',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'West Vancouver Patio Covers | LoomiHome Patios',
    metaDescription:
      'Patio covers in West Vancouver — Ambleside, Dundarave, British Properties, and nearby. Compare covers with a fast estimate and free measurement.',
    h1: 'Patio Covers in West Vancouver',
    intro:
      'West Vancouver patios often prioritize views, rain protection, and a clean modern look. Start with a fast rough estimate, compare glass, aluminum, or combo systems, then schedule a free measurement when the range fits your plans.',
    highlights: [
      'Glass and combo options for brighter outdoor spaces',
      'Engineered for coastal rain and wind exposure',
      'Rough quote first, free measurement second',
      'Lower Mainland team familiar with West Van sites',
    ],
    localAngle:
      'Lot grade, view lines, and attachment details matter on the North Shore. Enter your size in our Design & Price tool first; photos and the free measurement confirm the final price.',
    pricingNote:
      'Premium finishes and larger spans can move price. We keep the first ballpark plain and practical.',
    faqs: [
      {
        q: 'Do you serve West Vancouver and North Vancouver?',
        a: 'Yes — both are regular service areas for our installation team.',
      },
      {
        q: 'Is glass popular in West Vancouver?',
        a: 'Many homeowners compare glass for light and views versus aluminum for value and rain protection. We walk you through both in the online Design & Price tool.',
      },
      {
        q: 'How much does a West Vancouver patio cover cost?',
        a: PRICING_COPY.aluminumGlassCompare,
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-west-vancouver', label: 'Patio cover contractor in West Vancouver' },
      { path: '/glass-patio-covers-west-vancouver', label: 'Glass patio covers in West Vancouver' },
      { path: '/aluminum-patio-covers-west-vancouver', label: 'Aluminum patio covers in West Vancouver' },
      { path: '/patio-cover-cost-west-vancouver', label: 'Patio cover cost in West Vancouver' },
      { path: '/projects/west-vancouver-glass-patio-cover', label: 'West Vancouver glass project' },
      { path: '/projects/west-vancouver-aluminum-patio-cover', label: 'West Vancouver aluminum project' },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'West Coast glass patio cover — planning reference for West Vancouver view lines and walkout decks',
      caption:
        'Glass patio cover example used for West Vancouver planning — bright rain protection for view lines and walkout decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/west-vancouver-glass-patio-cover',
    },
  },
  'new-westminster': {
    id: 'new-westminster',
    path: '/patio-covers-new-westminster',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'New Westminster Patio Covers | LoomiHome Patios',
    metaDescription:
      'Patio covers in New Westminster — Queensborough, Sapperton, Uptown, and nearby. Fast rough estimate, compare cover types, free on-site measurement.',
    h1: 'Patio Covers in New Westminster',
    intro:
      'New Westminster townhomes and detached homes both benefit from covered patios through wet seasons. Use our Design & Price tool to compare aluminum, glass, or combo patio cover planning ranges, then book a free measurement for formal pricing.',
    highlights: [
      'Works for compact patios and larger backyards',
      'Fast online estimate before scheduling visits',
      'Rain-ready systems for Metro Vancouver weather',
      'Serving New West and surrounding cities',
    ],
    localAngle:
      'From Queens Park to the riverfront, patio sizes vary. City and approximate dimensions are enough to start a useful range.',
    pricingNote:
      'Attachment and height assumptions are confirmed on site during the free measurement visit.',
    faqs: [
      {
        q: 'Do you install patio covers in New Westminster?',
        a: 'Yes — Queensborough, Sapperton, Uptown, and nearby New Westminster neighbourhoods are part of our regular Metro Vancouver service area.',
      },
      {
        q: 'How much does a patio cover cost in New Westminster?',
        a: 'Our Design & Price tool gives a planning total for your size. See the New Westminster cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'Is glass or aluminum better for compact New Westminster patios?',
        a: 'Aluminum is often the practical rain-first option on tighter townhome decks; glass keeps more light when living rooms face the patio. We compare both — and skyline combo — in the online Design & Price tool.',
      },
      {
        q: 'Can I get pricing before a home visit?',
        a: 'Yes — that is how our estimate flow is designed.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-new-westminster', label: 'Patio cover contractor in New Westminster' },
      { path: '/aluminum-patio-covers-new-westminster', label: 'Aluminum patio covers in New Westminster' },
      { path: '/glass-patio-covers-new-westminster', label: 'Glass patio covers in New Westminster' },
      { path: '/skyline-combo-patio-covers-new-westminster', label: 'Skyline combo covers in New Westminster' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
      { path: '/projects/new-westminster-aluminum-patio-cover', label: 'New Westminster aluminum project' },
      { path: '/projects/new-westminster-glass-patio-cover', label: 'New Westminster glass project' },
    ],
    caseStudy: {
      image: '/house/skyline/p40.jpg',
      alt: 'Skyline combo patio cover — planning reference for New Westminster townhome and detached patios',
      caption:
        'Skyline combo cover example used for New Westminster planning — practical fit for compact townhome patios and detached backyards. Use our Design & Price tool to see a planning range for your dimensions.',
      projectPath: '/projects/new-westminster-skyline-combo-cover',
    },
  },
  'maple-ridge': {
    id: 'maple-ridge',
    path: '/patio-covers-maple-ridge',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Maple Ridge | LoomiHome Patios',
    metaDescription:
      'Patio covers in Maple Ridge — Town Centre, Albion, Silver Valley, and nearby. Compare aluminum, glass, and combo covers online; sunroom pricing follows a free on-site measurement.',
    h1: 'Patio Covers in Maple Ridge',
    intro:
      'Maple Ridge homeowners often want more usable backyard space before summer and through the rainy season. Start with an online planning range from our Design & Price tool, compare cover types, then book a free on-site measurement when you are ready for exact numbers.',
    highlights: [
      'Good fit for larger Maple Ridge lots',
      'Compare aluminum, glass, combo, and sunroom paths',
      'Ballpark first — no pressure to book immediately',
      'Lower Mainland crew serving the Fraser Valley edge',
    ],
    localAngle:
      'Silver Valley, Albion, and Hammond areas have different lot sizes and deck setups. Chat helps narrow the right product before we visit.',
    pricingNote:
      'Span, posts, and drainage details are confirmed during the free measurement.',
    faqs: [
      {
        q: 'Do you serve Maple Ridge and Pitt Meadows?',
        a: 'Yes — both are within our Metro Vancouver / Lower Mainland service area, including Town Centre, Albion, and Silver Valley.',
      },
      {
        q: 'How much does a patio cover cost in Maple Ridge?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Maple Ridge cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'Should I choose aluminum, glass, or a skyline combo on a larger Maple Ridge lot?',
        a: 'Aluminum is usually the rain-first budget option; glass keeps more light; skyline combo mixes shade and brightness. We can ballpark all three from the same approximate size.',
      },
      {
        q: 'How do I start a patio cover quote?',
        a: 'Use the Design & Price tool on our homepage with your city and approximate patio size to get a planning range in about a minute.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-maple-ridge', label: 'Patio cover contractor in Maple Ridge' },
      { path: '/aluminum-patio-covers-maple-ridge', label: 'Aluminum patio covers in Maple Ridge' },
      { path: '/glass-patio-covers-maple-ridge', label: 'Glass patio covers in Maple Ridge' },
      { path: '/skyline-combo-patio-covers-maple-ridge', label: 'Skyline combo covers in Maple Ridge' },
      { path: '/sunrooms-maple-ridge', label: 'Sunrooms in Maple Ridge' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/projects/maple-ridge-aluminum-patio-cover', label: 'Maple Ridge aluminum project' },
      { path: '/projects/maple-ridge-glass-patio-cover', label: 'Maple Ridge glass project' },
    ],
    caseStudy: {
      image: '/house/before-after/surrey-skyline-after.png',
      alt: 'Fraser Valley skyline combo patio cover — planning reference for Maple Ridge lots',
      caption:
        'Skyline combo cover — larger backyard layout common in Maple Ridge. Compare aluminum and glass on your dimensions in the online Design & Price tool.',
      projectPath: '/projects/maple-ridge-skyline-combo-cover',
    },
  },
  'pitt-meadows': {
    id: 'pitt-meadows',
    path: '/patio-covers-pitt-meadows',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Pitt Meadows | LoomiHome Patios',
    metaDescription:
      'Patio covers in Pitt Meadows — fast rough estimate online, compare aluminum, glass, and combo options, free on-site measurement across the Lower Mainland.',
    h1: 'Patio Covers in Pitt Meadows',
    intro:
      'Pitt Meadows homeowners looking for patio covers usually want a practical price range first. Use our Design & Price tool to compare aluminum, glass, or combo systems, then schedule a free measurement when the numbers make sense.',
    highlights: [
      'Rain-ready patio covers for Fraser Valley weather',
      'Fast estimate without waiting on callbacks',
      'Free on-site measurement for final pricing',
      'Serving Pitt Meadows, Maple Ridge, and nearby',
    ],
    localAngle:
      'Many Pitt Meadows homes have room for wider spans. Entering approximate width and projection in our Design & Price tool makes the first price more accurate.',
    pricingNote:
      'Final pricing always follows an on-site measure — the chat number is for planning.',
    faqs: [
      {
        q: 'Do you install patio covers in Pitt Meadows?',
        a: 'Yes — Pitt Meadows and nearby Maple Ridge are part of our regular Lower Mainland coverage.',
      },
      {
        q: 'How much does a patio cover cost in Pitt Meadows?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Pitt Meadows cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'What cover type is best for rainy Fraser Valley weather?',
        a: 'Aluminum and combo covers are popular for solid rain protection; glass works when you want more light on wider spans. Compare planning ranges in the Design & Price tool using your approximate size.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-pitt-meadows', label: 'Patio cover contractor in Pitt Meadows' },
      { path: '/aluminum-patio-covers-pitt-meadows', label: 'Aluminum patio covers in Pitt Meadows' },
      { path: '/glass-patio-covers-pitt-meadows', label: 'Glass patio covers in Pitt Meadows' },
      { path: '/patio-cover-cost-pitt-meadows', label: 'Patio cover cost in Pitt Meadows' },
      { path: '/projects/pitt-meadows-aluminum-patio-cover', label: 'Pitt Meadows aluminum project' },
      { path: '/projects/pitt-meadows-glass-patio-cover', label: 'Pitt Meadows glass project' },
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Fraser Valley aluminum patio cover — planning reference for wider Pitt Meadows lots',
      caption:
        'Aluminum patio cover example used for Pitt Meadows planning — practical rain protection for wider spans. Online price before free measurement.',
      projectPath: '/projects/pitt-meadows-aluminum-patio-cover',
    },
  },
  'port-coquitlam': {
    id: 'port-coquitlam',
    path: '/patio-covers-port-coquitlam',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Port Coquitlam | LoomiHome Patios',
    metaDescription:
      'Patio covers in Port Coquitlam — Citadel Heights, Mary Hill, and nearby Tri-Cities. Fast rough quote, compare aluminum, glass, and combo covers.',
    h1: 'Patio Covers in Port Coquitlam',
    intro:
      'Port Coquitlam homeowners often want a patio that handles Tri-Cities rain without weeks of quote chasing. Enter your city, approximate size, and cover type in the online Design & Price tool to see a patio cover planning range. Sunroom pricing is confirmed after a free on-site measurement of the buildable area and site conditions.',
    highlights: [
      'Tri-Cities rain-ready aluminum, glass, and combo options',
      'Ballpark pricing before anyone visits your home',
      'Helpful for townhomes, yards, and raised decks',
      'Free on-site measurement for final pricing',
    ],
    localAngle:
      'From Citadel Heights to Mary Hill, Port Coquitlam lots mix townhomes and detached homes. Share approximate size and how you use the patio — the Design & Price tool gives a planning range before we measure on site.',
    pricingNote:
      'Attachment, slope, and finish level change the final number. We explain that clearly after your initial ballpark.',
    faqs: [
      {
        q: 'Do you install patio covers in Port Coquitlam?',
        a: 'Yes — Port Coquitlam and the wider Tri-Cities are part of our regular Lower Mainland coverage.',
      },
      {
        q: 'How much does a patio cover cost in Port Coquitlam?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Port Coquitlam cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'How fast can I get a patio cover estimate in Port Coquitlam?',
        a: 'The online Design & Price tool gives most homeowners a planning range in about a minute after they enter their city and approximate size.',
      },
      {
        q: 'Do you also serve Coquitlam and Port Moody?',
        a: 'Yes — we work across the Tri-Cities. Use the Coquitlam or Port Moody city pages if you want area-specific examples.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-port-coquitlam', label: 'Patio cover contractor in Port Coquitlam' },
      { path: '/aluminum-patio-covers-port-coquitlam', label: 'Aluminum patio covers in Port Coquitlam' },
      { path: '/glass-patio-covers-port-coquitlam', label: 'Glass patio covers in Port Coquitlam' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/projects/port-coquitlam-aluminum-patio-cover', label: 'Port Coquitlam aluminum project' },
      { path: '/projects/port-coquitlam-glass-patio-cover', label: 'Port Coquitlam glass project' },
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Tri-Cities glass patio cover — planning reference for Port Coquitlam yards',
      caption:
        'Glass patio cover — rain-ready option for Port Coquitlam yards. Compare aluminum on the same size in the online Design & Price tool.',
      projectPath: '/projects/port-coquitlam-glass-patio-cover',
    },
  },
  'port-moody': {
    id: 'port-moody',
    path: '/patio-covers-port-moody',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Patio Covers Port Moody | LoomiHome Patios',
    metaDescription:
      'Patio covers in Port Moody — Moody Centre, Suter Brook, and nearby. Fast rough estimate, compare aluminum, glass, and combo patio covers.',
    h1: 'Patio Covers in Port Moody',
    intro:
      'Port Moody homeowners searching for patio covers usually want rain protection that still feels bright — especially on compact lots near the inlet. Use our Design & Price tool to compare aluminum, glass, or skyline combo systems, then schedule a free measurement for formal pricing.',
    highlights: [
      'Rain-ready covers for coastal Tri-Cities weather',
      'Compare glass and aluminum planning ranges in the Design & Price tool',
      'Fast estimate without waiting on callbacks',
      'Free on-site measurement for final pricing',
    ],
    localAngle:
      'Moody Centre, Suter Brook, and Newport Village patios vary in width and exposure. Entering your dimensions in our Design & Price tool makes the first price more useful before we visit.',
    pricingNote:
      'Final pricing always follows an on-site measure — the chat number is for planning.',
    faqs: [
      {
        q: 'Do you install patio covers in Port Moody?',
        a: 'Yes — Port Moody is part of our regular Metro Vancouver and Tri-Cities coverage.',
      },
      {
        q: 'Is glass or aluminum better for Port Moody rain?',
        a: 'Both can work when designed with proper slope and drainage. Aluminum is often the budget-friendly rain option; glass keeps more light. Compare their planning ranges in the Design & Price tool using your size.',
      },
      {
        q: 'Do you serve Port Coquitlam and Coquitlam too?',
        a: 'Yes — see our Port Coquitlam and Coquitlam pages for nearby Tri-Cities examples and contractor pages.',
      },
      {
        q: 'How much does a patio cover cost in Port Moody?',
        a: 'It depends on size, product, view and slope conditions, and attachment. Our Design & Price tool gives a planning total from rough dimensions; the Port Moody patio cover cost guide explains what moves the price, and free measurement confirms the formal quote.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-port-moody', label: 'Patio cover contractor in Port Moody' },
      { path: '/aluminum-patio-covers-port-moody', label: 'Aluminum patio covers in Port Moody' },
      { path: '/glass-patio-covers-port-moody', label: 'Glass patio covers in Port Moody' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
      { path: '/projects/port-moody-aluminum-patio-cover', label: 'Port Moody aluminum project' },
      { path: '/projects/port-moody-glass-patio-cover', label: 'Port Moody glass project' },
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
    ],
    caseStudy: {
      image: '/house/before-after/coquitlam-glass-after.png',
      alt: 'Tri-Cities glass patio cover — planning reference for Port Moody walkout decks',
      caption:
        'Glass patio cover — bright rain protection suited to Port Moody walkout decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/port-moody-glass-patio-cover',
    },
  },
  abbotsford: {
    id: 'abbotsford',
    path: '/patio-covers-abbotsford',
    heroImage: '/house/Aluminum/aluminum-hero.png',
    metaTitle: 'Patio Covers Abbotsford | LoomiHome Patios',
    metaDescription:
      'Patio covers in Abbotsford — Clearbrook, Sumas Mountain, and nearby Fraser Valley. Fast rough quote, compare aluminum, glass, and combo covers.',
    h1: 'Patio Covers in Abbotsford',
    intro:
      'Abbotsford homeowners often want a backyard cover that handles Fraser Valley rain without weeks of quote chasing. Enter your city, approximate size, and cover type in the online Design & Price tool to see a patio cover planning range. Sunroom pricing is confirmed after a free on-site measurement of the buildable area and site conditions.',
    highlights: [
      'Fraser Valley rain-ready aluminum, glass, and combo options',
      'Ballpark pricing before anyone visits your home',
      'Helpful for larger lots and family backyards',
      'Free on-site measurement for final pricing',
    ],
    localAngle:
      'From Clearbrook to Sumas Mountain, Abbotsford lots are often roomier than city yards. Share approximate size and how you use the patio — the Design & Price tool gives a planning range before we measure on site.',
    pricingNote:
      'Attachment, slope, and finish level change the final number. We explain that clearly after your initial ballpark.',
    faqs: [
      {
        q: 'Do you install patio covers in Abbotsford?',
        a: 'Yes — Clearbrook, Sumas Mountain, Downtown Abbotsford, and nearby Fraser Valley communities are part of our Lower Mainland coverage.',
      },
      {
        q: 'How much does a patio cover cost in Abbotsford?',
        a: 'Our Design & Price tool gives a planning total for your size. See the Abbotsford cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'How fast can I get a patio cover estimate in Abbotsford?',
        a: 'The online Design & Price tool gives most homeowners a planning range in about a minute after they enter their city and approximate size.',
      },
      {
        q: 'Should I compare aluminum or glass for an Abbotsford backyard?',
        a: 'Aluminum is usually the rain-first budget option on larger lots; glass keeps more light when living rooms face the patio. We compare both in the online Design & Price tool using the same size.',
      },
      {
        q: 'Do you also serve Langley and White Rock?',
        a: 'Yes — we work across the Lower Mainland and Fraser Valley. Use the Langley or White Rock city pages if you want area-specific examples.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-abbotsford', label: 'Patio cover contractor in Abbotsford' },
      { path: '/aluminum-patio-covers-abbotsford', label: 'Aluminum patio covers in Abbotsford' },
      { path: '/glass-patio-covers-abbotsford', label: 'Glass patio covers in Abbotsford' },
      { path: '/patio-cover-cost-abbotsford', label: 'Patio cover cost in Abbotsford' },
      { path: '/projects/abbotsford-aluminum-patio-cover', label: 'Abbotsford aluminum project' },
      { path: '/projects/abbotsford-glass-patio-cover', label: 'Abbotsford glass project' },
      { path: '/patio-covers-langley', label: 'Patio covers in Langley' },
    ],
    caseStudy: {
      image: '/house/before-after/burnaby-aluminum-after.png',
      alt: 'Fraser Valley aluminum patio cover — planning reference for Abbotsford family backyards',
      caption:
        'Aluminum patio cover example used for Abbotsford planning — rain-ready coverage for larger family lots. Compare glass on your dimensions in the online Design & Price tool.',
      projectPath: '/projects/abbotsford-aluminum-patio-cover',
    },
  },
  'white-rock': {
    id: 'white-rock',
    path: '/patio-covers-white-rock',
    heroImage: '/house/glass/glass-hero.png',
    metaTitle: 'Patio Covers White Rock | LoomiHome Patios',
    metaDescription:
      'Patio covers in White Rock — East Beach, West Beach, and nearby South Surrey. Fast rough estimate, compare aluminum, glass, and combo patio covers.',
    h1: 'Patio Covers in White Rock',
    intro:
      'White Rock homeowners searching for patio covers usually want rain and wind protection that still feels bright — especially on compact seaside lots. Use our Design & Price tool to compare aluminum, glass, or skyline combo systems, then schedule a free measurement for formal pricing.',
    highlights: [
      'Coastal rain- and wind-ready cover options',
      'Compare glass and aluminum planning ranges in the Design & Price tool',
      'Fast estimate without waiting on callbacks',
      'Free on-site measurement for final pricing',
    ],
    localAngle:
      'East Beach, West Beach, and nearby South Surrey patios vary in width and ocean exposure. Entering your dimensions in our Design & Price tool makes the first price more useful before we visit.',
    pricingNote:
      'Final pricing always follows an on-site measure — the chat number is for planning.',
    faqs: [
      {
        q: 'Do you install patio covers in White Rock?',
        a: 'Yes — White Rock and nearby South Surrey are part of our regular Lower Mainland coverage.',
      },
      {
        q: 'How much does a patio cover cost in White Rock?',
        a: 'Our Design & Price tool gives a planning total for your size. See the White Rock cost guide for local cost factors, then book free measurement for a formal quote.',
      },
      {
        q: 'Is glass or aluminum better for White Rock coastal weather?',
        a: 'Both can work when designed with proper slope, drainage, and attachment. Aluminum is often the practical rain option; glass keeps more light. Compare their planning ranges in the Design & Price tool using your size.',
      },
      {
        q: 'Do you serve Surrey and Abbotsford too?',
        a: 'Yes — see our Surrey and Abbotsford pages for nearby Fraser Valley and South Surrey examples.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-cover-contractor-white-rock', label: 'Patio cover contractor in White Rock' },
      { path: '/aluminum-patio-covers-white-rock', label: 'Aluminum patio covers in White Rock' },
      { path: '/glass-patio-covers-white-rock', label: 'Glass patio covers in White Rock' },
      { path: '/patio-cover-cost-white-rock', label: 'Patio cover cost in White Rock' },
      { path: '/projects/white-rock-aluminum-patio-cover', label: 'White Rock aluminum project' },
      { path: '/projects/white-rock-glass-patio-cover', label: 'White Rock glass project' },
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
    ],
    caseStudy: {
      image: '/house/before-after/vancouver-glass-after.png',
      alt: 'Coastal glass patio cover — planning reference for White Rock and South Surrey decks',
      caption:
        'Glass patio cover example used for White Rock planning — bright rain protection for seaside decks. Compare aluminum for budget in the online Design & Price tool.',
      projectPath: '/projects/white-rock-glass-patio-cover',
    },
  },
};
